import { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageInput — image picker with client-side resize.
 * Saves the result as a data:image/jpeg;base64,... URL into the form field.
 *
 * Props:
 *  - value: current image URL or data URL
 *  - onChange: (newValue: string) => void
 *  - testid: prefix for data-testid
 *  - maxWidth: optional, default 1000
 *  - quality: optional 0..1, default 0.85
 */
export default function ImageInput({ value, onChange, testid, maxWidth = 1000, quality = 0.85 }) {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const isHeic =
      /\.(heic|heif)$/i.test(file.name) ||
      file.type === "image/heic" ||
      file.type === "image/heif";
    if (!file.type.startsWith("image/") && !isHeic) {
      toast.error("Please choose an image file.");
      return;
    }
    setLoading(true);
    try {
      let working = file;
      if (isHeic) {
        // Convert HEIC/HEIF to JPEG blob (iPhone photos)
        const { default: convert } = await import("heic-convert/browser");
        const buffer = new Uint8Array(await file.arrayBuffer());
        const output = await convert({ buffer, format: "JPEG", quality: 0.92 });
        const blob = new Blob([output], { type: "image/jpeg" });
        working = new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
      }
      const dataUrl = await resizeImage(working, maxWidth, quality);
      onChange(dataUrl);
    } catch (e) {
      console.error("Image processing failed:", e);
      toast.error("Couldn't process that image. Try another one.");
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const clear = () => {
    onChange("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt="Preview"
            className="max-h-48 max-w-full object-contain border border-voltage-blue/40 bg-black/40 p-2"
          />
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-clear`}
            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 hover:bg-red-600"
            aria-label="Remove image"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-voltage-blue/40 hover:border-voltage-yellow transition-colors p-6 flex flex-col items-center justify-center gap-2 text-center cursor-pointer"
          onClick={() => fileRef.current?.click()}
          data-testid={`${testid}-dropzone`}
        >
          <ImageIcon className="w-8 h-8 text-voltage-muted" />
          <div className="text-sm text-voltage-muted">
            {loading ? "Processing..." : "Drop an image here or click to choose"}
          </div>
          <div className="text-xs text-voltage-muted font-mono">JPG · PNG · WEBP · HEIC — auto-resized</div>
        </div>
      )}

      <div className="flex items-center gap-3 text-xs">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid={`${testid}-pick`}
          className="inline-flex items-center gap-1.5 text-voltage-yellow hover:text-white font-display font-bold uppercase tracking-widest"
        >
          <Upload className="w-3.5 h-3.5" /> {value ? "Replace" : "Upload"}
        </button>
        <span className="text-voltage-muted">·</span>
        <button
          type="button"
          onClick={() => setShowUrl((s) => !s)}
          data-testid={`${testid}-toggle-url`}
          className="text-voltage-muted hover:text-white font-display font-bold uppercase tracking-widest"
        >
          {showUrl ? "Hide URL field" : "Or paste a URL"}
        </button>
      </div>

      {showUrl && (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-url`}
          placeholder="https://..."
          className="w-full bg-black/50 border border-voltage-blue/40 focus:border-voltage-yellow outline-none px-3 py-2 text-white font-mono text-sm"
        />
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*,.heic,.heif"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testid}-file`}
      />
    </div>
  );
}

/**
 * Resize an image file via canvas and return a data URL (JPEG).
 */
function resizeImage(file, maxWidth, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch (e) {
          reject(e);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
